import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';

export type PopoverAlign = 'start' | 'end';

export interface PopoverProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: PopoverAlign;
  triggerClassName?: string;
  contentClassName?: string;
  className?: string;
  /** Render the content in document.body so a clipping ancestor cannot hide it. */
  portal?: boolean;
  'aria-label'?: string;
}

export function Popover({
  trigger,
  children,
  open: controlledOpen,
  onOpenChange,
  align = 'start',
  triggerClassName,
  contentClassName,
  className,
  portal = false,
  'aria-label': ariaLabel,
}: PopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const containerRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = React.useState<DOMRect | null>(null);
  const [contentWidth, setContentWidth] = React.useState<number | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const wasOpenRef = React.useRef(open);
  const restoreFocusRef = React.useRef(false);

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  React.useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !contentRef.current?.contains(target)
      ) {
        restoreFocusRef.current = false;
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        restoreFocusRef.current = true;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, setOpen]);

  React.useEffect(() => {
    if (wasOpenRef.current && !open && restoreFocusRef.current) {
      triggerRef.current?.focus();
    }
    if (!open) restoreFocusRef.current = false;
    wasOpenRef.current = open;
  }, [open]);

  React.useLayoutEffect(() => {
    if (!open || !portal) return;
    const update = () => {
      if (triggerRef.current) setAnchor(triggerRef.current.getBoundingClientRect());
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, portal]);

  React.useLayoutEffect(() => {
    if (!open) {
      setContentWidth(null);
      return;
    }
    if (!portal || !anchor || !contentRef.current) return;
    setContentWidth(contentRef.current.getBoundingClientRect().width);
  }, [open, portal, anchor]);

  const portalStyle = (): React.CSSProperties | undefined => {
    if (!portal || !anchor) return undefined;
    if (contentWidth === null) return { top: anchor.bottom + 4, left: 0, visibility: 'hidden' };
    const margin = 8;
    const preferred = align === 'end' ? anchor.right - contentWidth : anchor.left;
    const max = window.innerWidth - contentWidth - margin;
    return { top: anchor.bottom + 4, left: Math.max(margin, Math.min(preferred, max)) };
  };

  const content = (
    <div
      ref={contentRef}
      role="dialog"
      aria-label={ariaLabel}
      className={cn(
        'z-50 min-w-[12rem] rounded-md border bg-popover p-2 text-popover-foreground shadow-md',
        portal ? 'fixed' : cn('absolute mt-1', align === 'end' ? 'right-0' : 'left-0'),
        contentClassName,
      )}
      style={portalStyle()}
    >
      {children}
    </div>
  );

  return (
    <div ref={containerRef} className={cn('relative inline-block', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen(!open)}
        className={cn(
          'inline-flex items-center justify-center rounded-md text-body ring-offset-background',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          triggerClassName,
        )}
      >
        {trigger}
      </button>
      {open && (portal ? (anchor ? createPortal(content, document.body) : null) : content)}
    </div>
  );
}
